/**
 * SavedOrderPopup Component
 * Modal popup for managing saved orders for a specific restaurant
 * Shows list of saved orders with ability to add/edit/delete
 */

import React, { useState, useEffect } from 'react';
import { X, Save, Trash2, DollarSign, Plus, BookMarked, Edit } from 'lucide-react';
import { savedOrdersService } from '../utils/savedOrdersService';
import { logger } from '../utils/logger';

const SavedOrderPopup = ({
  isOpen,
  onClose,
  restaurant,
  customerId
}) => {
  const [savedOrders, setSavedOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingOrder, setEditingOrder] = useState(null); // null = list view, object = editing
  const [isCreating, setIsCreating] = useState(false);

  // Form state
  const [orderTemplateName, setOrderTemplateName] = useState('Default Order');
  const [orderItems, setOrderItems] = useState('');
  const [averageOrderSize, setAverageOrderSize] = useState('');
  const [orderNotes, setOrderNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Load saved orders for this restaurant
  useEffect(() => {
    if (isOpen && restaurant && customerId) {
      loadSavedOrders();
    }
  }, [isOpen, restaurant, customerId]);

  const loadSavedOrders = async () => {
    try {
      setLoading(true);
      const searchKey = restaurant.yelp_business_id || restaurant.name;
      const orders = await savedOrdersService.getSavedOrdersByRestaurant(customerId, searchKey);
      setSavedOrders(orders || []);
    } catch (error) {
      logger.error('Error loading saved orders', { error: error.message });
      setSavedOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNewOrder = () => {
    setIsCreating(true);
    setEditingOrder({});
    setOrderTemplateName('Default Order');
    setOrderItems('');
    setAverageOrderSize('');
    setOrderNotes('');
    setError('');
  };

  const handleEditOrder = (order) => {
    setIsCreating(false);
    setEditingOrder(order);
    setOrderTemplateName(order.order_template_name || 'Default Order');
    setOrderItems(order.order_items || '');
    setAverageOrderSize(order.average_order_size || '');
    setOrderNotes(order.order_notes || '');
    setError('');
  };

  const handleCancelEdit = () => {
    setEditingOrder(null);
    setIsCreating(false);
    setError('');
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      if (!orderItems.trim()) {
        setError('Please enter order details');
        return;
      }

      const orderData = {
        order_template_name: orderTemplateName,
        order_items: orderItems,
        average_order_size: averageOrderSize ? parseFloat(averageOrderSize) : null,
        order_notes: orderNotes
      };

      if (isCreating) {
        // Create new order
        const newOrderData = {
          customerId: customerId,
          restaurantYelpId: restaurant?.yelp_business_id || null,
          restaurantName: restaurant?.name || 'Restaurant',
          restaurantAddress: restaurant?.address || null,
          restaurantPhone: restaurant?.phone || null,
          restaurantData: restaurant || null,
          orderTemplateName: orderData.order_template_name,
          orderItems: orderData.order_items,
          averageOrderSize: orderData.average_order_size,
          orderNotes: orderData.order_notes
        };

        await savedOrdersService.createSavedOrder(newOrderData);
        logger.debug('New saved order created');
      } else {
        // Update existing order
        await savedOrdersService.updateSavedOrder(editingOrder.id, orderData);
        logger.debug('Saved order updated', { orderId: editingOrder.id });
      }

      // Reload orders and return to list view
      await loadSavedOrders();
      handleCancelEdit();
    } catch (error) {
      logger.error('Error saving order', { error: error.message });
      setError(error.message || 'Failed to save order');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this saved order?')) {
      return;
    }

    try {
      await savedOrdersService.deleteSavedOrder(orderId);
      await loadSavedOrders();
      if (editingOrder && editingOrder.id === orderId) {
        handleCancelEdit();
      }
    } catch (error) {
      logger.error('Error deleting saved order', { error: error.message });
      setError('Failed to delete order');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingOrder ? (isCreating ? 'Add Saved Order' : 'Edit Saved Order') : 'Manage Saved Orders'}
              </h2>
              <p className="text-sm text-gray-600 mt-1">{restaurant?.name}</p>
              {restaurant?.address && (
                <p className="text-xs text-gray-500 mt-1">{restaurant.address}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 ml-4"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : editingOrder !== null ? (
            /* Edit/Create Form */
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Order Name
                </label>
                <input
                  type="text"
                  value={orderTemplateName}
                  onChange={(e) => setOrderTemplateName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Default Order, Large Meeting, Light Lunch"
                  maxLength={100}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Order Details *
                </label>
                <textarea
                  value={orderItems}
                  onChange={(e) => setOrderItems(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  rows={10}
                  placeholder="Enter your typical order details...
Example:
- 3x Grilled Chicken Salad
- 2x Veggie Wrap (no cheese)
- 1x Salmon Bowl
- Dressing on the side"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Average Order Size
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="number"
                    step="0.01"
                    value={averageOrderSize}
                    onChange={(e) => setAverageOrderSize(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Special Instructions
                </label>
                <textarea
                  value={orderNotes}
                  onChange={(e) => setOrderNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Any special instructions or notes..."
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div className="flex items-center gap-3 pt-4">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2"
                >
                  <Save className="w-4 h-4" />
                  <span>{saving ? 'Saving...' : 'Save Order'}</span>
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            /* List View */
            <div>
              {savedOrders.length === 0 ? (
                <div className="text-center py-12">
                  <BookMarked className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-600 mb-2">No saved orders for this restaurant yet</p>
                  <p className="text-sm text-gray-500 mb-6">
                    Save your typical orders for quick reference during meetings
                  </p>
                  <button
                    onClick={handleAddNewOrder}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add First Order
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Add New Button */}
                  {savedOrders.length < 3 && (
                    <button
                      onClick={handleAddNewOrder}
                      className="w-full border-2 border-dashed border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg p-4 flex items-center justify-center gap-2 transition-colors"
                    >
                      <Plus className="w-5 h-5" />
                      <span className="font-medium">Add New Order ({savedOrders.length}/3)</span>
                    </button>
                  )}

                  {/* Saved Orders List */}
                  {savedOrders.map((order) => (
                    <div
                      key={order.id}
                      className="border border-blue-200 bg-blue-50 rounded-lg p-4"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-blue-900">{order.order_template_name}</h3>
                          {order.average_order_size && (
                            <p className="text-sm text-blue-700 mt-1">
                              Average: ${parseFloat(order.average_order_size).toFixed(2)}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEditOrder(order)}
                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg"
                            title="Edit order"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(order.id)}
                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
                            title="Delete order"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      <div className="mb-3 bg-white p-3 rounded border border-blue-200">
                        <p className="text-sm text-blue-800 whitespace-pre-wrap font-mono">
                          {order.order_items}
                        </p>
                      </div>

                      {order.order_notes && (
                        <div className="bg-blue-100 p-2 rounded text-xs text-blue-700">
                          <span className="font-medium">Special Instructions: </span>
                          {order.order_notes}
                        </div>
                      )}

                      {order.times_used > 0 && (
                        <p className="text-xs text-blue-600 mt-2">
                          Used {order.times_used} time{order.times_used !== 1 ? 's' : ''}
                          {order.last_used && ` • Last: ${new Date(order.last_used).toLocaleDateString()}`}
                        </p>
                      )}
                    </div>
                  ))}

                  {savedOrders.length >= 3 && (
                    <p className="text-sm text-gray-500 text-center italic">
                      Maximum of 3 saved orders per restaurant reached
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SavedOrderPopup;
